import { useCallback, useEffect, useMemo, useState } from 'react';
import { KpiService } from './kpiService';
import { ClientKpiConfiguration, DashboardKpi, KpiDefinition, KpiFilters, KpiResult } from './types';

export const useKpiDashboard = (filters: KpiFilters) => {
    const [definitions, setDefinitions] = useState<KpiDefinition[]>([]);
    const [configuration, setConfiguration] = useState<ClientKpiConfiguration[]>([]);
    const [results, setResults] = useState<KpiResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);
        Promise.all([
            KpiService.getAvailableKpis(),
            KpiService.getClientKpiConfiguration(filters.dashboardTabId),
            KpiService.getKpiResults(filters),
        ]).then(([nextDefinitions, nextConfiguration, nextResults]) => {
            if (!active) return;
            setDefinitions(nextDefinitions);
            setConfiguration(nextConfiguration);
            setResults(nextResults);
        }).catch(() => active && setError(true)).finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [filters.dashboardTabId, filters.programId, filters.schoolYearId, filters.termTypeId]);

    const kpis = useMemo<DashboardKpi[]>(() => definitions.flatMap((definition) => {
        return configuration.filter((item) => item.kpiId === definition.id && item.enabled).flatMap((config) => {
            const result = results.find((item) => item.kpiId === definition.id && item.viewType === config.viewType);
            return result ? [{ ...definition, ...config, ...result }] : [];
        });
    }).sort((a, b) => a.displayOrder - b.displayOrder), [configuration, definitions, results]);

    const toggleKpi = useCallback((kpiId: string, viewType: ClientKpiConfiguration['viewType']) => {
        setConfiguration((current) => {
            const updated = current.map((item) => item.kpiId === kpiId && item.viewType === viewType ? { ...item, enabled: !item.enabled } : item);
            KpiService.saveClientKpiConfiguration(updated).catch((saveError) => console.error('Failed to save KPI configuration:', saveError));
            return updated;
        });
    }, []);

    const disableAllKpis = useCallback(() => {
        setConfiguration((current) => {
            const updated = current.map((item) => ({ ...item, enabled: false }));
            KpiService.saveClientKpiConfiguration(updated).catch((saveError) => console.error('Failed to save KPI configuration:', saveError));
            return updated;
        });
    }, []);

    return { definitions, configuration, kpis, loading, error, toggleKpi, disableAllKpis };
};
